import qs from 'query-string';
import { prop, omit, assocPath, pathOr, keys, pipe, filter, complement, propEq } from 'ramda';

import { ID, FUNDUS_IMAGES, EYE_EXTERIOR_IMAGES } from 'store/examinations';

import { wrapperRequest } from './utils';
import { uploadGCloudFile } from './uploads';
import { API_URL, URL, POST, PATCH, SUB_PATIENT, EXPAND } from './consts';
import { getCache, UPLOAD_FILES_CACHE, FILES_MAP_CACHE } from './cache';

const defParams = { [EXPAND]: [SUB_PATIENT] };

export const postExamination = (body = {}) =>
  wrapperRequest({
    url: `${API_URL}/examinations?${qs.stringify(defParams)}`,
    options: { method: POST, body },
  });

export const getExaminations = (query = {}) =>
  wrapperRequest({
    url: `${API_URL}/examinations?${qs.stringify({ ...query, ...defParams })}`,
  });

export const getExamination = (id, query = {}) =>
  wrapperRequest({
    url: `${API_URL}/examinations/${id}?${qs.stringify(query)}`,
  });

export const patchExamination = (body = {}) =>
  wrapperRequest({
    url: `${API_URL}/examinations/${prop(ID, body)}?${qs.stringify(defParams)}`,
    options: { method: PATCH, body: omit([ID], body) },
  });

export const uploadFileToGCloud = async (file, examId, feature) =>
  uploadGCloudFile(
    file,
    (feature[1] === EYE_EXTERIOR_IMAGES && 'eye_exterior') || (feature[1] === FUNDUS_IMAGES && 'fundus') || feature[1]
  );

export const uploadFileToCache = async (file, examinationId, examinationPath) => {
  const filesCache = await getCache(UPLOAD_FILES_CACHE);
  const mapCache = await getCache(FILES_MAP_CACHE);

  if (!(filesCache && mapCache)) return null;

  const { name, size, type, lastModified } = file;
  const fileName = `${size}_${lastModified}_${name}`;
  const cacheUrl = `${URL}/cache_upload/${fileName}`;

  try {
    const hasMatch = await mapCache.match(cacheUrl);

    if (hasMatch) return null;

    const fileMap = {
      id: cacheUrl,
      name,
      originalname: name,
      mimetype: type,
      bucket: [examinationId, ...examinationPath].join('.'),
      size,
      url: cacheUrl,
      offline: true,
    };

    await filesCache.put(new Request(cacheUrl, { 'Content-Type': type }), new Response(file));
    await mapCache.put(cacheUrl, new Response(JSON.stringify(fileMap)));

    return fileMap;
  } catch (e) {
    throw Error('An error occurred when writing file to cache.');
  }
};

export const deleteFileFromCache = async (cacheUrl) => {
  const filesCache = await getCache(UPLOAD_FILES_CACHE);
  const mapCache = await getCache(FILES_MAP_CACHE);

  if (!(filesCache && mapCache)) return;

  try {
    await filesCache.delete(cacheUrl);
    await mapCache.delete(cacheUrl);
  } catch (e) {
    console.error('An error occurred when deleting file from cache.'); // eslint-disable-line no-console
  }
};

const getCacheFiles = async () => {
  try {
    const cacheMap = await getCache(FILES_MAP_CACHE);
    const cacheFiles = await getCache(UPLOAD_FILES_CACHE);
    if (!(cacheMap && cacheFiles)) return { error: 'An error occurred when opening caches.' };

    const allFilesMap = await cacheMap.matchAll();
    const mapFiles = await Promise.all(allFilesMap.map((response) => response.json()));
    const allFiles = await Promise.all(
      mapFiles.map((fileMap) =>
        cacheFiles
          .match(fileMap.url)
          .then((r) => r && r.blob())
          .then((blob) => blob && { ...fileMap, file: new File([blob], fileMap.originalname, { type: fileMap.mimetype }) })
      )
    );

    return { files: allFiles.filter(Boolean) };
  } catch (e) {
    const error = 'An error occurred when reading file from cache.';

    console.error(error); // eslint-disable-line no-console

    return { error };
  }
};

export const uploadFilesFromCache = async () => {
  const { files, error } = await getCacheFiles();

  if (error) return { error };
  if (!files.length) return {};

  const examIds = files.reduce((acc, { bucket }) => {
    const [examinationId] = bucket.split('.');
    if (examinationId && !acc.includes(examinationId)) acc.push(examinationId);
    return acc;
  }, []);
  const pathsForDelete = [];

  try {
    const examinations = await Promise.all(examIds.map((examId) => getExamination(examId, defParams)));
    const examinationsMap = examinations.reduce((acc, exam) => {
      acc[exam[ID]] = exam;
      return acc;
    }, {});

    const promises = files.map(({ id, bucket, file }) => async () => {
      const [examinationId, side, area] = bucket.split('.');
      const filesPath = [side, area];
      const examinationFiles = pathOr([], [examinationId, side, area], examinationsMap);
      const fileIndex = examinationFiles.findIndex((item) => item.offline && item.id === id);

      if (fileIndex > -1) {
        const data = await uploadFileToGCloud(file, examinationId, filesPath);
        examinationsMap[examinationId] = assocPath(
          filesPath,
          [...pipe(pathOr([], filesPath), filter(complement(propEq(id, 'id'))))(examinationsMap[examinationId]), data],
          examinationsMap[examinationId]
        );
        pathsForDelete.push(id);
      }
    });

    await Promise.all(promises.map((promise) => promise()));
    await Promise.all(keys(examinationsMap).map((examId) => patchExamination(examinationsMap[examId])));
    await Promise.all(pathsForDelete.map((cacheUrl) => deleteFileFromCache(cacheUrl)));

    return { success: 'All files uploaded successfully.' };
  } catch (e) {
    const err = 'An error occurred when saving file to cache.';

    console.error(err, e); // eslint-disable-line no-console

    return { error: err };
  }
};
